import { useEffect, useRef, useState } from 'react';
import {
  deriveObjectivePlan,
  ModelTask,
  OBJECTIVE_DATA_MODALITIES,
  OBJECTIVE_DATA_ON_HAND,
  OBJECTIVE_DEPLOY_TARGETS,
  OBJECTIVE_SUPPORTS,
  type Objective,
  type ObjectiveDataModality,
  type ObjectiveDataOnHand,
  type ObjectiveDeployTarget,
  type ObjectiveSupport,
  type PlanStep,
} from '@ddp/shared';
import Button from '@carbon/react/es/components/Button';
import Modal from '@carbon/react/es/components/Modal/Modal';
import Select from '@carbon/react/es/components/Select/Select';
import SelectItem from '@carbon/react/es/components/SelectItem/SelectItem';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import RadioButtonGroup from '@carbon/react/es/components/RadioButtonGroup/RadioButtonGroup';
import RadioButton from '@carbon/react/es/components/RadioButton/RadioButton';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';

import { useT } from '../../i18n/index.js';
import { HelpHint } from '../../components/HelpHint.js';
import { useObjective } from './use-objective.js';
import {
  BRANCH_LABEL,
  DATA_ON_HAND_LABEL,
  DEPLOY_LABEL,
  MODALITY_LABEL,
  STATE_LABEL,
  STEP_LABEL,
  SUPPORT_LABEL,
  TASK_LABEL,
} from './labels.js';

type FormState = {
  targetsText: string;
  dataModality: ObjectiveDataModality;
  support: ObjectiveSupport;
  deployTarget: ObjectiveDeployTarget;
  dataOnHand: ObjectiveDataOnHand;
};

function initialForm(objective: Objective | null): FormState {
  return {
    targetsText: objective?.targets.join(', ') ?? '',
    dataModality: objective?.dataModality ?? 'OPTICAL',
    support: objective?.support ?? 'VIDEO_STREAM',
    deployTarget: objective?.deployTarget ?? 'DRONE',
    dataOnHand: objective?.dataOnHand ?? 'NONE',
  };
}

/**
 * Panneau « Objectif » du détail projet (ADR-0028, incrément 1b). Sans objectif : une invite à en
 * définir un (rôle éditeur). Avec objectif : son récapitulatif + le **plan guidé** dérivé de
 * `deriveObjectivePlan` (même fonction que l'API — l'avancement se lit sur les données, jamais stocké).
 * Seule la **détection** est ouverte ; les autres tâches apparaissent « à venir ».
 */
export function ObjectivePanel({
  projectId,
  canManage,
  autoOpen = false,
}: {
  projectId: string;
  canManage: boolean;
  /** Ouvre d'emblée le formulaire (arrivée depuis « Démarrer un objectif projet », `#objectif`). */
  autoOpen?: boolean;
}) {
  const t = useT();
  const { objective, signals, loading, save, clear } = useObjective(projectId);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<FormState>(initialForm(null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openForm = () => {
    setForm(initialForm(objective));
    setError(null);
    setEditing(true);
  };

  // Ouverture automatique une seule fois, une fois l'objectif chargé (pour pré-remplir en édition).
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpen && canManage && !loading && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setForm(initialForm(objective));
      setError(null);
      setEditing(true);
    }
  }, [autoOpen, canManage, loading, objective]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await save({
        task: ModelTask.DETECTION,
        targets: form.targetsText
          .split(',')
          .map((x) => x.trim())
          .filter((x) => x.length > 0),
        dataModality: form.dataModality,
        support: form.support,
        deployTarget: form.deployTarget,
        dataOnHand: form.dataOnHand,
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setBusy(false);
    }
  };

  const plan = objective
    ? deriveObjectivePlan(
        {
          task: objective.task,
          targets: objective.targets,
          dataOnHand: objective.dataOnHand,
        },
        signals,
      )
    : null;

  return (
    <section className="ddp-objective" aria-label={t('objective.title')}>
      <div className="ddp-objective__head">
        <h2 className="ddp-project-detail__subtitle">
          {t('objective.title')}
          <HelpHint textKey="help.objective" />
        </h2>
        {canManage && objective && (
          <div className="ddp-objective__head-actions">
            <Button size="sm" kind="ghost" onClick={openForm}>
              {t('objective.edit')}
            </Button>
            <Button size="sm" kind="danger--ghost" onClick={() => void clear()}>
              {t('objective.remove')}
            </Button>
          </div>
        )}
      </div>

      {loading ? null : !objective ? (
        <div className="ddp-objective__empty">
          <p>{t('objective.empty')}</p>
          {canManage && (
            <Button size="sm" onClick={openForm}>
              {t('objective.define')}
            </Button>
          )}
        </div>
      ) : (
        <>
          <dl className="ddp-objective__recap">
            <div>
              <dt>{t('objective.form.task')}</dt>
              <dd>{t(TASK_LABEL[objective.task])}</dd>
            </div>
            <div>
              <dt>{t('objective.form.targets')}</dt>
              <dd>{objective.targets.length ? objective.targets.join(', ') : '—'}</dd>
            </div>
            <div>
              <dt>{t('objective.form.modality')}</dt>
              <dd>{t(MODALITY_LABEL[objective.dataModality])}</dd>
            </div>
            <div>
              <dt>{t('objective.form.support')}</dt>
              <dd>{t(SUPPORT_LABEL[objective.support])}</dd>
            </div>
            <div>
              <dt>{t('objective.form.deployTarget')}</dt>
              <dd>{t(DEPLOY_LABEL[objective.deployTarget])}</dd>
            </div>
            <div>
              <dt>{t('objective.form.dataOnHand')}</dt>
              <dd>{t(DATA_ON_HAND_LABEL[objective.dataOnHand])}</dd>
            </div>
          </dl>

          <h3 className="ddp-objective__plan-title">{t('objective.plan.title')}</h3>
          <ol className="ddp-objective__plan">
            {plan?.steps.map((step) => (
              <PlanRow key={step.id} step={step} />
            ))}
          </ol>
        </>
      )}

      {editing && (
        <Modal
          open
          size="sm"
          modalHeading={objective ? t('objective.edit') : t('objective.define')}
          primaryButtonText={busy ? t('data.action.saving') : t('overview.projects.save')}
          secondaryButtonText={t('overview.projects.cancel')}
          primaryButtonDisabled={busy}
          onRequestClose={() => setEditing(false)}
          onRequestSubmit={() => void submit()}
        >
          <div className="ddp-objective__form">
            <RadioButtonGroup
              legendText={t('objective.form.task')}
              name="objective-task"
              valueSelected={ModelTask.DETECTION}
              onChange={() => undefined}
            >
              {(Object.keys(TASK_LABEL) as ModelTask[]).map((task) => (
                <RadioButton
                  key={task}
                  id={`objective-task-${task}`}
                  value={task}
                  disabled={task !== ModelTask.DETECTION}
                  labelText={
                    task === ModelTask.DETECTION
                      ? t(TASK_LABEL[task])
                      : `${t(TASK_LABEL[task])} · ${t('objective.task.soon')}`
                  }
                />
              ))}
            </RadioButtonGroup>

            <TextInput
              id="objective-targets"
              labelText={t('objective.form.targets')}
              helperText={t('objective.form.targetsHint')}
              placeholder={t('objective.form.targetsPlaceholder')}
              value={form.targetsText}
              onChange={(e) => setForm({ ...form, targetsText: e.target.value })}
            />

            <Select
              id="objective-modality"
              labelText={t('objective.form.modality')}
              value={form.dataModality}
              onChange={(e) =>
                setForm({ ...form, dataModality: e.target.value as ObjectiveDataModality })
              }
            >
              {OBJECTIVE_DATA_MODALITIES.map((m) => (
                <SelectItem key={m} value={m} text={t(MODALITY_LABEL[m])} />
              ))}
            </Select>

            <Select
              id="objective-support"
              labelText={t('objective.form.support')}
              value={form.support}
              onChange={(e) => setForm({ ...form, support: e.target.value as ObjectiveSupport })}
            >
              {OBJECTIVE_SUPPORTS.map((s) => (
                <SelectItem key={s} value={s} text={t(SUPPORT_LABEL[s])} />
              ))}
            </Select>

            <Select
              id="objective-deploy"
              labelText={t('objective.form.deployTarget')}
              value={form.deployTarget}
              onChange={(e) =>
                setForm({ ...form, deployTarget: e.target.value as ObjectiveDeployTarget })
              }
            >
              {OBJECTIVE_DEPLOY_TARGETS.map((d) => (
                <SelectItem key={d} value={d} text={t(DEPLOY_LABEL[d])} />
              ))}
            </Select>

            <Select
              id="objective-dataonhand"
              labelText={t('objective.form.dataOnHand')}
              value={form.dataOnHand}
              onChange={(e) =>
                setForm({ ...form, dataOnHand: e.target.value as ObjectiveDataOnHand })
              }
            >
              {OBJECTIVE_DATA_ON_HAND.map((d) => (
                <SelectItem key={d} value={d} text={t(DATA_ON_HAND_LABEL[d])} />
              ))}
            </Select>

            {error && (
              <InlineNotification
                kind="error"
                title={t('common.error')}
                subtitle={error}
                lowContrast
                hideCloseButton
              />
            )}
          </div>
        </Modal>
      )}
    </section>
  );
}

/** Une étape du plan : libellé, état, jauge de progression et branches d'action. */
function PlanRow({ step }: { step: PlanStep }) {
  const t = useT();
  const pct = step.meter
    ? Math.min(100, Math.round((step.meter.have / step.meter.target) * 100))
    : 0;
  return (
    <li className={`ddp-objective__step is-${step.state}`}>
      <span className="ddp-objective__step-node" aria-hidden="true" />
      <div className="ddp-objective__step-card">
        <div className="ddp-objective__step-top">
          <span className="ddp-objective__step-title">
            {t(STEP_LABEL[step.id] ?? 'objective.title')}
          </span>
          <span className={`ddp-objective__state is-${step.state}`}>
            {t(STATE_LABEL[step.state])}
          </span>
        </div>
        {step.meter && (
          <div className="ddp-objective__meter">
            <div className="ddp-objective__meter-row">
              <span>{t('objective.step.datasetUnit')}</span>
              <span>
                <b>{step.meter.have}</b> / {step.meter.target} · {pct}%
              </span>
            </div>
            <div className="ddp-objective__meter-bar">
              <div className="ddp-objective__meter-fill" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
        {step.branches && step.branches.length > 0 && (
          <div className="ddp-objective__branches">
            {step.branches.map((b) => (
              <span
                key={b.id}
                className={`ddp-objective__branch${b.recommended ? ' is-reco' : ''}`}
              >
                {t(BRANCH_LABEL[b.id] ?? 'objective.title')}
                {b.recommended && (
                  <span className="ddp-objective__reco">{t('objective.branch.recommended')}</span>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}
